import React from 'react';
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ReferenceLine, ResponsiveContainer, XAxis, YAxis, } from 'recharts';
import { DeviationChartLegend } from './DeviationChartLegend';
import { makeStyles } from '@material-ui/styles';
import { ChartTooltip } from '../../ChartTooltip';
import { useDashboardTooltip } from '../../../../../../hooks';
import { useModal } from '@topconagriculture/modal';
import { ChartArrow } from '../../ChartArrow';
import { useRedirect } from '../../../../../../hooks/useRedirect';
import { useDashboardDeviationSubscription } from '../../hooks/useDashboardDeviationSubscription';
import { useWeightUnit } from '../../../../../../hooks/useGetMetric';
const useStyles = makeStyles(() => ({
    chartWrapper: { height: 'calc(100% - 47px)' },
}));
const renderCustomTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { offset } = payload;
    const pathX = Math.floor(x - offset) + 0.5;
    return payload.index === 0 ? (React.createElement("text", null, "''")) : (React.createElement("path", { d: `M${pathX},${y - 40}v${-460}`, stroke: "#c1c1c1", strokeDasharray: "5,5" }));
};
const alertColors = { lower: '#E46A68', upper: '#007DC5', noAlert: '#6C6C6C' };
const getColor = (entry) => {
    const value = entry.value;
    const tolerance = entry.tolerance[1];
    if (value < 0 && Math.abs(value) > tolerance) {
        return alertColors.lower;
    }
    if (value > 0 && value > tolerance) {
        return alertColors.upper;
    }
    return alertColors.noAlert;
};
const Shape = (entry) => {
    const { x, y, width, height } = entry;
    return (React.createElement("g", null,
        React.createElement("rect", { x: x, y: height > 0 ? y : y + height, width: width, height: Math.abs(height), fill: getColor(entry) })));
};
const Arrow = (props) => {
    const { x, width, value, tolerance } = props;
    const cx = x + width / 2;
    const limit = tolerance * 5;
    const showArrow = Math.abs(value) > limit;
    return showArrow ? React.createElement(ChartArrow, { cx: cx, deviation: value, yPositions: [67, 490], barWidth: width }) : null;
};
export const DeviationBarchart = ({ chartData, domain, title = '' }) => {
    const classes = useStyles();
    const { modalParams } = useModal();
    const { data } = useDashboardDeviationSubscription(modalParams.id, modalParams.widget.type);
    const weightUnit = useWeightUnit();
    const { openTooltip, closeTooltip } = useDashboardTooltip();
    const goToIngredientLoadReport = useRedirect();
    const showTooltip = (e) => openTooltip(tooltipContent(e));
    const onBarClick = (e) => {
        const payload = e.payload;
        const item = Object.values(payload).find((barData) => barData.deviation === e.value);
        closeTooltip();
        goToIngredientLoadReport(`/reports/specific/loadreport?rations=${item.rationId}&date=${item.date}&allExpanded=true`);
    };
    const barKeys = chartData.reduce((acc, curr) => {
        Object.keys(curr).forEach((key) => {
            if (key !== 'date' && key !== 'tolerance') {
                acc.add(key);
            }
        });
        return acc;
    }, new Set());
    const bars = Array.from(barKeys).map((key) => (React.createElement(Bar, { key: key, dataKey: `${key}.deviation`, onMouseEnter: showTooltip, onMouseLeave: closeTooltip, onClick: onBarClick, shape: Shape, cursor: "pointer" },
        React.createElement(LabelList, { content: React.createElement(Arrow, { tolerance: data === null || data === void 0 ? void 0 : data.tolerance }) }))));
    const tooltipContent = (e) => {
        const payload = e.payload;
        const id = Object.values(payload).find((barData) => barData.deviation === e.value).id;
        const ingredientData = data === null || data === void 0 ? void 0 : data.items.find((item) => item.id === id);
        const { deviation, deviationPerc, rationName, dateTime, driverName } = ingredientData;
        return (React.createElement(ChartTooltip, { data: {
                deviation,
                deviationPerc,
                rationName,
                dateTime,
                driverName,
                tolerance: (data === null || data === void 0 ? void 0 : data.tolerance) || 0,
                farmParams: modalParams.farmParams,
            }, weightUnit: weightUnit }));
    };
    return (React.createElement("div", { className: classes.chartWrapper },
        React.createElement(ResponsiveContainer, { width: "100%", height: "100%" },
            React.createElement(BarChart, { width: 600, height: 400, data: chartData, margin: { top: 20, right: 30, left: 20, bottom: 5 }, maxBarSize: 200 },
                React.createElement(CartesianGrid, { vertical: false }),
                React.createElement(XAxis, { dataKey: "date" }),
                React.createElement(XAxis, { dataKey: "date", tick: renderCustomTick, axisLine: false, tickLine: false, height: 0.00001, xAxisId: "secondXAxis" }),
                React.createElement(YAxis, { domain: domain, allowDataOverflow: true, label: { value: weightUnit, position: 'insideTopRight', dy: -30 } }),
                React.createElement(Legend, { content: React.createElement(DeviationChartLegend, { title: title }), verticalAlign: "top" }),
                React.createElement(ReferenceLine, { y: 0, stroke: "#46BC01" }),
                bars))));
};
//# sourceMappingURL=DeviationBarchart.js.map